export const abbreviateNumber = (
  num: number
): { num: number; abbreviation: string } => {
  if (num >= 1e12) {
    return { num: num / 1e12, abbreviation: "t" };
  } else if (num >= 1e9) {
    return { num: num / 1e9, abbreviation: "b" };
  } else if (num >= 1e6) {
    return { num: num / 1e6, abbreviation: "m" };
  } else if (num >= 1e3) {
    return { num: num / 1e3, abbreviation: "k" };
  }

  return { num, abbreviation: "" };
};

type FormatOptions = {
  round?: boolean;
  precision?: number;
};

const formatSingleNumber = (
  num: number,
  options: FormatOptions = {}
): string => {
  const { round = false, precision = 2 } = options;

  let abbreviation;
  if (round) {
    const data = abbreviateNumber(num);
    num = data.num;
    abbreviation = data.abbreviation;
  }

  const parts = num.toString().split(".");

  if (parts[1]) {
    parts[1] = parts[1].slice(0, precision);
  }

  const truncatedNumber = parts.join(".");

  const formattedNum = Number(truncatedNumber).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
  });

  if (round) {
    return `${formattedNum}${abbreviation}`;
  }

  return formattedNum;
};

export const formatNumber = (
  value: number | number[],
  options: FormatOptions = {}
): string => {
  if (Array.isArray(value)) {
    return value.map((v) => formatSingleNumber(v, options)).join(" - ");
  }

  return formatSingleNumber(value, options);
};

export const formatEllipsis = (
  value: string,
  startLength = 3,
  endLength = 4
): string => {
  if (!value || value.length <= startLength + endLength + 3) return value;
  const start = value.slice(0, startLength);
  const end = value.slice(-endLength);
  return `${start}...${end}`;
};

export const formatDateLocalized = (
  dateStr: string,
  locale: string
): string => {
  const date = new Date(dateStr);

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(date);
};

export const formatDateRecordLocalized = (
  dateStr: string,
  locale: string
): string => {
  const date = new Date(dateStr);

  const formattedDate = date.toLocaleDateString(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return `${formattedDate} • ${formattedTime}`;
};

export const formatDateRecordLocalizedDateOnly = (
  dateStr: string,
  locale: string
): string => {
  const date = new Date(dateStr);

  const formattedDate = date.toLocaleDateString(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  return formattedDate;
};

export function formatDateWithSeconds(dateStr: string, locale: string): string {
  const date = new Date(dateStr);

  const datePart = date.toLocaleDateString(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const formattedDate = datePart.replace(/,?\s+/g, "-").replace(/-г\.-?/g, "");

  return `${formattedDate} ${timePart}`;
}

export function formatDateWithSecondsDateOnly(dateStr: string, locale: string): string {
  const date = new Date(dateStr);

  const datePart = date.toLocaleDateString(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const formattedDate = datePart.replace(/,?\s+/g, "-").replace(/-г\.-?/g, "");

  return formattedDate;
}

export const formatDateToLongString = (
  dateString: string,
  locale: string = "en"
): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
